/** @format */

import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import RateInterface from '../../../interfaces/billing/RateInterface';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { configDefault } from '../../../core/config';
import Loader from '../../../components/Loaders/Loader';
import { LinearBillingInterface } from '../../../interfaces/billing/LinearBillingInterface';
import { RateCall } from '../Rate/RateCall';
import { RateService } from '../Rate/RateService';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import CheckBoxSimple from '../../../components/Form/CheckBoxSimple';
import RateRangesCustomerSummaricInterface from '../../../interfaces/billing/RateRangesCustomerSummaricInterface';
import { RateRanges } from '../Rate/RateRanges';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import I18n from '../../../components/Translate/I18n';
import SimpleSelectRest from '../../../components/Adescom/SimpleSelectRest';
import { RateHelper } from '../../../helpers/RateHelper';

interface RateTabProps {
    linearBilling: LinearBillingInterface;
    rate: RateInterface | undefined;
    updateRate: (rate: RateInterface) => void;
    updateRateRanges: (rateRanges: RateRangesCustomerSummaricInterface[]) => void;
    rateRanges: RateRangesCustomerSummaricInterface[] | undefined;
    updateUsesRateRanges: (usesRateRanges: boolean) => void;
    usesRateRanges: boolean;
    updateRateRangesWereLoaded: (rateRangesWereLoaded: boolean) => void;
}

export const RateTab = (props: RateTabProps): React.ReactElement => {
    const dispatch = useDispatch();
    const linearBilling = props.linearBilling;
    const rate = props.rate;
    const updateRate = props.updateRate;
    const updateRateRanges = props.updateRateRanges;
    const rateRanges = props.rateRanges;
    const updateUsesRateRanges = props.updateUsesRateRanges;
    const usesRateRanges = props.usesRateRanges;
    const updateRateRangesWereLoaded = props.updateRateRangesWereLoaded;

    const subTypeCollection = useMemo((): { 'hydra:member': Array<{ name: string; value: number }> } => {
        return { 'hydra:member': RateHelper.mapRateConstantsNoUndefined() };
    }, []);

    const [rateLoaded, setRateLoaded] = useState<boolean>(false);

    const allowedRateTypes = useMemo((): Array<number> => {
        if (linearBilling.type === 0) {
            return [0, 1, 2];
        } else {
            return [3];
        }
    }, [linearBilling]);

    const loadRate = useCallback(() => {
        if (!linearBilling.linearBillingId) {
            return;
        }

        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`/rates`, configDefault.PLATFORM_API_PREFIX)),
            'RATE_GET_LIST',
            [{ name: 'linearBillingId', value: linearBilling.linearBillingId }],
            [],
            (data: any) => {
                setRateLoaded(true);
                if (data && data['hydra:member'] && data['hydra:member'].length > 0) {
                    if (data['hydra:member'][0].type && !allowedRateTypes.includes(data['hydra:member'][0].type)) {
                        let rateType = 0;
                        if (linearBilling.type === 1) {
                            rateType = 3;
                        }

                        const tmpRate = ({
                            id: -1,
                            hourRange: 24,
                            countingUnit: 1,
                            price1: '0.00',
                            initial: '0.00',
                            interval1: 1,
                            interval2: 1,
                            price2: '0.00',
                            duration1: 1,
                            type: rateType,
                            days: null,
                            useRatio: false,
                            byDiscount: false,
                        } as unknown) as RateInterface;
                        updateRate(tmpRate);
                    } else {
                        updateRate(data['hydra:member'][0]);
                        setRateLoaded(true);
                    }
                }
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [linearBilling, dispatch, updateRate, allowedRateTypes]);

    useEffect(() => {
        if (!rate && linearBilling.linearBillingId === -1) {
            let rateType = 0;
            if (linearBilling.type === 1) {
                rateType = 3;
            }
            const tmpRate = ({
                id: -1,
                hourRange: 24,
                countingUnit: 1,
                price1: '0.00',
                initial: '0.00',
                interval1: 1,
                interval2: 1,
                price2: '0.00',
                duration1: 1,
                type: rateType,
                days: null,
                useRatio: false,
                byDiscount: false,
            } as unknown) as RateInterface;
            updateRate(tmpRate);
            setRateLoaded(true);
            return;
        }

        if (!rate && linearBilling.linearBillingId !== -1) {
            loadRate();
        } else {
            setRateLoaded(true);
        }
    }, [linearBilling, loadRate, updateRate, rate]);

    const changeRateInput = useCallback(
        (e: any, stateName: string): void => {
            if (!stateName || stateName === undefined) {
                stateName = e.target.name;
            }
            let tmpRate: Record<string, any> = { ...rate } as Record<string, any>;
            tmpRate[stateName] = e.target.value;
            updateRate((tmpRate as unknown) as RateInterface);
        },
        [rate, updateRate]
    );
    const changeRateSelect = useCallback(
        (e: any, stateName: string): void => {
            let tmpRate: Record<string, any> = { ...rate } as Record<string, any>;
            tmpRate[stateName] = e.target.value;
            updateRate((tmpRate as unknown) as RateInterface);
        },
        [rate, updateRate]
    );

    const changeProportionality = useCallback((): void => {
        let tmpRate: Record<string, any> = { ...rate } as Record<string, any>;
        tmpRate['useRatio'] = !tmpRate['useRatio'];
        updateRate((tmpRate as unknown) as RateInterface);
    }, [rate, updateRate]);

    const subTypeCheckboxCallback = useCallback(
        (event: any): void => {
            let tmpRate: Record<string, any> = { ...rate } as Record<string, any>;
            if (!event.target.checked) {
                tmpRate['subType'] = null;
            } else {
                tmpRate['subType'] = subTypeCollection['hydra:member'][0].value;
            }
            updateRate((tmpRate as unknown) as RateInterface);
        },
        [rate, updateRate, subTypeCollection]
    );

    const updateSubType = useCallback(
        (event: any): void => {
            let tmpRate: Record<string, any> = { ...rate } as Record<string, any>;
            tmpRate['subType'] = event.target.value;
            updateRate((tmpRate as unknown) as RateInterface);
        },
        [rate, updateRate]
    );

    return (
        <>
            <Loader visible={!rateLoaded} />
            {rate && (
                <GridContainer>
                    <GridItem xs={6}>
                        <GridContainer spacing={0}>
                            <GridItem xs={12}>
                                {linearBilling.type === 0 && (
                                    <RateCall
                                        changeRateInput={changeRateInput}
                                        changeRateSelect={changeRateSelect}
                                        rateEntity={rate}
                                        rateEntityStatus={(): void => {}}
                                    />
                                )}
                                {linearBilling.type === 1 && (
                                    <RateService changeRateInput={changeRateInput} rateEntity={rate} rateEntityStatus={(): void => {}} />
                                )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem xs={6}>
                        <CheckBoxSimple
                            checked={rate.useRatio ?? false}
                            label={'pricelists:h.count_proportionally'}
                            callback={changeProportionality}
                        />
                        <RateRanges
                            usesRateRanges={usesRateRanges}
                            updateUsesRateRanges={updateUsesRateRanges}
                            updateRateRanges={updateRateRanges}
                            rateRanges={rateRanges}
                            rate={rate}
                            updateRateRangesWereLoaded={updateRateRangesWereLoaded}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <GridContainer>
                            <GridItem xs={12}>
                                <FormControlLabel
                                    control={<Checkbox checked={!!rate.subType} onClick={subTypeCheckboxCallback} name="subType" color="primary" />}
                                    label={<I18n trans={'pricelists:h.rate_subtype'} />}
                                />
                            </GridItem>
                            <GridItem xs={12}></GridItem>
                            <GridItem xs={5}>
                                {!!rate.subType && (
                                    <SimpleSelectRest
                                        callback={updateSubType}
                                        restElements={subTypeCollection}
                                        stateName="subtype"
                                        idState="subtype"
                                        idSelected={rate.subType}
                                        propKey="value"
                                        required={false}
                                        propValue="name"
                                        emptyLabel="general:common.empty_label"
                                        label="pricelists:h.select_subtype"
                                    />
                                )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            )}
        </>
    );
};
